import { Fragment, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ShoppingBag, Search, SlidersHorizontal, ChevronLeft, ChevronRight, Share2, MessageCircle } from 'lucide-react';

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [selectedPhotoIndexes, setSelectedPhotoIndexes] = useState({});
  const [addingId, setAddingId] = useState(null);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchCategories = useCallback(async () => {
    const res = await API.get('/products/categories');
    setCategories(res.data);
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category_id = selectedCategory;
      if (search) params.search = search;
      const res = await API.get('/products/', { params });
      setProducts(res.data);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, search]);

  const fetchBanners = useCallback(async () => {
    try {
      const response = await API.get('/banners/');
      setBanners(response.data);
      setActiveBannerIndex(0);
    } catch {
      setBanners([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    if (loading) return;
    const sharedProductId = new URLSearchParams(window.location.search).get('product');
    if (!sharedProductId) return;
    window.setTimeout(() => document.getElementById(`product-${sharedProductId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, [loading, products]);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveBannerIndex(index => (index + 1) % banners.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [banners.length]);

  const handleAddToCart = async (product) => {
    if (!user) { navigate('/login'); return; }
    const size = selectedSizes[product.id] || null;
    const sizes = product.has_sizes ? product.size_options?.split(',').map(size => size.trim()).filter(Boolean) || [] : [];
    if (sizes.length > 0 && !size) {
      toast.error('Please select a size first');
      return;
    }
    setAddingId(product.id);
    try {
      await addToCart(product.id, 1, size);
      toast.success(`${product.name} added to cart!`);
    } catch {
      toast.error('Failed to add to cart');
    } finally {
      setAddingId(null);
    }
  };

  const shareProduct = async product => {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('product', product.id);
    const shareData = { title: product.name, text: `Have a look at ${product.name} at Kraftova`, url: url.toString() };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Product link copied');
      }
    } catch (error) {
      if (error.name !== 'AbortError') toast.error('Could not share product link');
    }
  };

  const activeBanner = banners[activeBannerIndex];
  const heroBackground = activeBanner
    ? `linear-gradient(${activeBanner.overlay_color}CC, ${activeBanner.overlay_color}66), url("${activeBanner.image_url}")`
    : 'radial-gradient(ellipse at 60% 50%, rgba(201,168,76,0.15) 0%, transparent 70%), var(--charcoal)';

  return (
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 70px)' }}>

      {/* Hero Banner */}
      <div style={{
        backgroundColor: 'var(--charcoal)',
        backgroundImage: heroBackground,
        backgroundSize: 'cover',
        backgroundPosition: activeBanner
          ? `${activeBanner.image_position_x}% ${activeBanner.image_position_y}%`
          : 'center',
        minHeight: '360px',
        padding: '4rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', zIndex: 1,
          left: `${activeBanner?.text_position_x ?? 50}%`,
          top: `${activeBanner?.text_position_y ?? 50}%`,
          transform: 'translate(-50%, -50%)',
          width: 'min(78%, 760px)',
          textAlign: activeBanner?.text_alignment || 'center',
          fontFamily: activeBanner?.font_family || 'var(--font-display)',
          color: activeBanner?.text_color || 'var(--white)',
          overflowWrap: 'anywhere',
        }}>
          <p style={{ color: activeBanner?.accent_color || 'var(--soft-gold)', fontWeight: '600', letterSpacing: '3px', fontSize: '0.8rem', marginBottom: '1rem', textTransform: 'uppercase' }}>
            {activeBanner?.kicker || 'New Season Collection'}
          </p>
          <h1 style={{
            fontFamily: activeBanner?.font_family || 'var(--font-display)',
            fontSize: `${activeBanner?.font_size || 56}px`,
            color: activeBanner?.text_color || 'var(--white)',
            marginBottom: '1rem', lineHeight: 1.2, whiteSpace: 'pre-line'
          }}>
            {activeBanner?.title || <>Dress for Every<br /><span style={{ color: 'var(--soft-gold)' }}>Occasion</span></>}
          </h1>
          <p style={{ color: activeBanner?.text_color || 'var(--mid-gray)', fontSize: '1rem', maxWidth: '500px', margin: '0 auto', whiteSpace: 'pre-line' }}>
            {activeBanner?.body || 'Curated styles for men, women & kids — quality you can feel.'}
          </p>
        </div>
        {banners.length > 1 && <>
          <button type="button" aria-label="Previous banner" onClick={() => setActiveBannerIndex(index => (index - 1 + banners.length) % banners.length)} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '40px', height: '40px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.65)', background: 'rgba(0,0,0,0.25)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><ChevronLeft size={22} /></button>
          <button type="button" aria-label="Next banner" onClick={() => setActiveBannerIndex(index => (index + 1) % banners.length)} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', width: '40px', height: '40px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.65)', background: 'rgba(0,0,0,0.25)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><ChevronRight size={22} /></button>
          <div aria-label="Choose banner" style={{ position: 'absolute', bottom: '1rem', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
            {banners.map((banner, index) => <button key={banner.id} type="button" aria-label={`Show banner ${index + 1}`} aria-current={activeBannerIndex === index} onClick={() => setActiveBannerIndex(index)} style={{ width: '9px', height: '9px', padding: 0, borderRadius: '50%', border: 0, background: activeBannerIndex === index ? (banner.accent_color || '#D6B24A') : 'rgba(255,255,255,0.6)', cursor: 'pointer' }} />)}
          </div>
        </>}
      </div>

      {/* Search + Filter Bar */}
      <div style={{
        background: 'var(--white)',
        padding: '1.2rem 2rem',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        position: 'sticky',
        top: '70px',
        zIndex: 100
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '360px' }}>
          <Search size={16} style={{
            position: 'absolute', left: '12px', top: '50%',
            transform: 'translateY(-50%)', color: 'var(--mid-gray)'
          }} />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem',
              border: '1.5px solid var(--light-gray)',
              borderRadius: '8px', fontSize: '0.9rem', outline: 'none',
              background: 'var(--cream)'
            }}
          />
        </div>

        {/* Category filters */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <SlidersHorizontal size={16} style={{ color: 'var(--mid-gray)' }} />
          <button
            onClick={() => setSelectedCategory(null)}
            style={{
              padding: '0.45rem 1rem', borderRadius: '20px', fontSize: '0.85rem',
              border: '1.5px solid',
              borderColor: !selectedCategory ? 'var(--charcoal)' : 'var(--light-gray)',
              background: !selectedCategory ? 'var(--charcoal)' : 'transparent',
              color: !selectedCategory ? 'var(--white)' : 'var(--charcoal)',
              fontWeight: '500', cursor: 'pointer', transition: 'var(--transition)'
            }}>
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '0.45rem 1rem', borderRadius: '20px', fontSize: '0.85rem',
                border: '1.5px solid',
                borderColor: selectedCategory === cat.id ? 'var(--charcoal)' : 'var(--light-gray)',
                background: selectedCategory === cat.id ? 'var(--charcoal)' : 'transparent',
                color: selectedCategory === cat.id ? 'var(--white)' : 'var(--charcoal)',
                fontWeight: '500', cursor: 'pointer', transition: 'var(--transition)'
              }}>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div style={{ padding: '2.5rem 2rem', maxWidth: '1300px', margin: '0 auto' }}>

        {/* Count */}
        <p style={{ color: 'var(--mid-gray)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          {loading ? 'Loading...' : `${products.length} product${products.length !== 1 ? 's' : ''} found`}
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--mid-gray)' }}>
            Loading products...
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--mid-gray)' }}>
            <ShoppingBag size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
            <p>No products found</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '1.8rem'
          }}>
            {products.map(product => {
              const sizes = product.has_sizes ? product.size_options?.split(',').map(size => size.trim()).filter(Boolean) || [] : [];
              const photos = product.image_urls?.length ? product.image_urls : (product.image_url ? [product.image_url] : []);
              const photoIndex = Math.min(selectedPhotoIndexes[product.id] || 0, Math.max(photos.length - 1, 0));
              const shareUrl = new URL(window.location.href);
              shareUrl.search = '';
              shareUrl.searchParams.set('product', product.id);
              const outOfStock = product.stock === 0;
              return (
                <div id={`product-${product.id}`} key={product.id} style={{
                  background: 'var(--white)',
                  borderRadius: 'var(--radius)',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'var(--transition)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                  onMouseOver={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}
                >
                  {/* Image */}
                  <div style={{ position: 'relative', aspectRatio: '1', background: 'var(--light-gray)' }}>
                    <img
                      src={photos[photoIndex] || 'https://via.placeholder.com/400x400?text=No+Image'}
                      alt={product.name}
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {photos.length > 1 && <>
                      <button type="button" aria-label={`Previous photo for ${product.name}`} onClick={() => setSelectedPhotoIndexes(current => ({ ...current, [product.id]: (photoIndex - 1 + photos.length) % photos.length }))} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', width: '34px', height: '34px', border: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.62)', color: 'white', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><ChevronLeft size={20} /></button>
                      <button type="button" aria-label={`Next photo for ${product.name}`} onClick={() => setSelectedPhotoIndexes(current => ({ ...current, [product.id]: (photoIndex + 1) % photos.length }))} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', width: '34px', height: '34px', border: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.62)', color: 'white', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><ChevronRight size={20} /></button>
                      <span style={{ position: 'absolute', right: '8px', bottom: '8px', padding: '0.2rem 0.45rem', borderRadius: '10px', background: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '0.72rem' }}>{photoIndex + 1} / {photos.length}</span>
                    </>}
                    {outOfStock && (
                      <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{
                          background: 'var(--danger)', color: 'white',
                          padding: '0.4rem 1rem', borderRadius: '20px',
                          fontWeight: '600', fontSize: '0.85rem'
                        }}>Out of Stock</span>
                      </div>
                    )}
                    {/* Category badge */}
                    {product.category && (
                      <span style={{
                        position: 'absolute', top: '10px', left: '10px',
                        background: 'rgba(28,28,30,0.75)',
                        color: 'var(--soft-gold)', padding: '0.25rem 0.7rem',
                        borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600'
                      }}>{product.category.name}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ padding: '1.1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{
                      fontSize: '1rem', fontWeight: '600',
                      marginBottom: '0.3rem', color: 'var(--charcoal)',
                      fontFamily: 'var(--font-display)'
                    }}>{product.name}</h3>
                    <p style={{
                      fontSize: '0.82rem', color: 'var(--mid-gray)',
                      marginBottom: '0.8rem', lineHeight: '1.4',
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>{product.description}</p>
                    {!!Object.keys(product.custom_fields || {}).length && <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 0.5rem', fontSize: '0.78rem', color: 'var(--mid-gray)', margin: '0 0 0.8rem' }}>
                      {Object.entries(product.custom_fields).slice(0, 4).map(([label, value]) => <Fragment key={label}><dt>{label}</dt><dd style={{ margin: 0, color: 'var(--charcoal)' }}>{value}</dd></Fragment>)}
                    </dl>}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                      <span style={{
                        fontSize: '1.2rem', fontWeight: '700',
                        color: 'var(--warm-brown)'
                      }}>₹{parseFloat(product.price).toFixed(2)}</span>
                      <span style={{ fontSize: '0.78rem', color: product.stock < 10 ? 'var(--danger)' : 'var(--success)', fontWeight: '500' }}>
                        {outOfStock ? 'Out of stock' : product.stock < 10 ? `Only ${product.stock} left` : `${product.stock} in stock`}
                      </span>
                    </div>

                    {/* Size Selector */}
                    {sizes.length > 0 && (
                      <div style={{ marginBottom: '0.9rem' }}>
                        <p style={{ fontSize: '0.78rem', color: 'var(--mid-gray)', marginBottom: '0.4rem', fontWeight: '500' }}>SIZE</p>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {sizes.map(size => (
                            <button
                              key={size}
                              onClick={() => setSelectedSizes(prev => ({ ...prev, [product.id]: size }))}
                              style={{
                                padding: '0.25rem 0.6rem',
                                border: '1.5px solid',
                                borderColor: selectedSizes[product.id] === size ? 'var(--charcoal)' : 'var(--light-gray)',
                                background: selectedSizes[product.id] === size ? 'var(--charcoal)' : 'transparent',
                                color: selectedSizes[product.id] === size ? 'var(--white)' : 'var(--charcoal)',
                                borderRadius: '6px', fontSize: '0.78rem',
                                fontWeight: '500', cursor: 'pointer',
                                transition: 'var(--transition)'
                              }}>{size.trim()}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add to Cart */}
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={outOfStock || addingId === product.id}
                      style={{
                        marginTop: 'auto',
                        width: '100%', padding: '0.7rem',
                        background: outOfStock ? 'var(--light-gray)' : 'var(--charcoal)',
                        color: outOfStock ? 'var(--mid-gray)' : 'var(--white)',
                        border: 'none', borderRadius: '8px',
                        fontWeight: '600', fontSize: '0.9rem',
                        cursor: outOfStock ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '6px',
                        transition: 'var(--transition)'
                      }}
                      onMouseOver={e => { if (!outOfStock) e.currentTarget.style.background = 'var(--warm-brown)'; }}
                      onMouseOut={e => { if (!outOfStock) e.currentTarget.style.background = 'var(--charcoal)'; }}
                    >
                      <ShoppingBag size={16} />
                      {addingId === product.id ? 'Adding...' : 'Add to Cart'}
                    </button>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.55rem' }}>
                      <button type="button" onClick={() => shareProduct(product)} aria-label={`Share ${product.name}`} title="Share product" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem', border: '1px solid var(--light-gray)', borderRadius: '7px', background: 'var(--white)', color: 'var(--charcoal)', cursor: 'pointer' }}><Share2 size={15} /> Share</button>
                      <a href={`https://wa.me/?text=${encodeURIComponent(`${product.name} - ${shareUrl.toString()}`)}`} target="_blank" rel="noreferrer" aria-label={`Share ${product.name} on WhatsApp`} title="Share on WhatsApp" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', border: '1px solid var(--light-gray)', borderRadius: '7px', color: '#168B52', background: 'var(--white)' }}><MessageCircle size={17} /></a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}